import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  IndianRupee,
  Check,
  X,
  Send,
  AlertCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DetailField } from '@/components/common/DetailField';
import {
  DIALOG_TAB_TRIGGER,
  DialogEyebrow,
  SectionHeading,
} from '@/components/common/DialogPrimitives';
import { refundsService } from '@/services/refunds';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Refund, RefundStatus } from '@/types/refunds';

interface Props {
  refund: Refund | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABEL: Record<RefundStatus, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  INITIATED: 'Initiated',
  PROCESSED: 'Processed',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
  PARTIAL_REFUND: 'Partial',
};

const STATUS_TONE: Record<RefundStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  REQUESTED: 'warning',
  APPROVED: 'warning',
  INITIATED: 'warning',
  PROCESSED: 'success',
  FAILED: 'destructive',
  REJECTED: 'destructive',
  PARTIAL_REFUND: 'success',
};

export function RefundDetailSheet({ refund, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<RefundStatus | null>(null);

  const detailQuery = useQuery({
    queryKey: ['refunds', 'detail', refund?.id],
    queryFn: () => refundsService.get(refund!.id),
    enabled: !!refund && open,
    initialData: refund ?? undefined,
    staleTime: 5_000,
  });
  const current = detailQuery.data ?? refund;

  const mutation = useMutation({
    mutationFn: (status: RefundStatus) =>
      refundsService.updateStatus(current!.id, { status }),
    onSuccess: (_d, status) => {
      toast.success(`Refund ${STATUS_LABEL[status].toLowerCase()}`);
      qc.invalidateQueries({ queryKey: ['refunds'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      setPending(null);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Could not update refund');
      setPending(null);
    },
  });

  const update = (next: RefundStatus) => {
    setPending(next);
    mutation.mutate(next);
  };

  const status = current?.status ?? 'REQUESTED';
  const isTerminal = ['PROCESSED', 'REJECTED', 'FAILED'].includes(status);
  const canApprove = status === 'REQUESTED';
  const canReject = ['REQUESTED', 'APPROVED'].includes(status);
  const canInitiate = status === 'APPROVED';
  const canMarkProcessed = status === 'INITIATED';
  const canMarkFailed = ['INITIATED'].includes(status);

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[88vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-7 pt-6 pb-5">
            <DialogTitle className="sr-only">{current?.refundNumber ?? 'Refund'}</DialogTitle>
            {current && (
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
                >
                  <IndianRupee className="h-6 w-6" />
                </span>

                <div className="min-w-0 flex-1 space-y-1.5">
                  <DialogEyebrow>
                    {current.type === 'STORE_CREDIT' ? 'Store credit' : `${current.type[0]}${current.type.slice(1).toLowerCase()} refund`}
                    {current.salesOrder ? ` · order ${current.salesOrder.orderNumber}` : ''} · {fmt.relative(current.createdAt)}
                  </DialogEyebrow>
                  <h2 className="font-mono text-[18px] font-semibold leading-tight tracking-tight text-foreground">
                    {current.refundNumber}
                  </h2>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={STATUS_TONE[status]} className="text-[10px]">
                      {STATUS_LABEL[status]}
                    </Badge>
                    <Badge variant="muted" className="text-[10px]">
                      {current.type}
                    </Badge>
                    <Badge variant="muted" className="font-mono text-[10px] tabular-nums">
                      {fmt.inr(Number(current.amount))}
                    </Badge>
                    {current.provider && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        {current.provider}
                      </Badge>
                    )}
                    {current.externalRefundRef && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        ref {current.externalRefundRef}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {canApprove && (
                    <Button
                      size="sm"
                      onClick={() => update('APPROVED')}
                      disabled={mutation.isPending}
                    >
                      <Check className="h-3.5 w-3.5" />
                      {pending === 'APPROVED' ? 'Approving…' : 'Approve'}
                    </Button>
                  )}
                  {canInitiate && (
                    <Button
                      size="sm"
                      onClick={() => update('INITIATED')}
                      disabled={mutation.isPending}
                    >
                      <Send className="h-3.5 w-3.5" />
                      {pending === 'INITIATED' ? 'Initiating…' : 'Initiate refund'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogHeader>
          <Separator />

          {/* Stats strip */}
          <div className="flex items-stretch divide-x divide-border/60 border-b border-border/60 bg-muted/20 px-7">
            <Stat label="Amount" value={fmt.inr(Number(current?.amount ?? 0))} accent />
            <Stat label="Type" value={current?.type ?? '—'} />
            <Stat
              label="Provider"
              value={current?.provider ?? '—'}
              muted
            />
            <Stat
              label="Initiated"
              value={current?.initiatedAt ? fmt.relative(current.initiatedAt) : '—'}
              muted
            />
            <Stat
              label="Processed"
              value={current?.processedAt ? fmt.relative(current.processedAt) : '—'}
              muted
            />
          </div>

          <Tabs defaultValue="overview" className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b border-border/60 bg-background px-7">
              <TabsList className="h-10 gap-2 bg-transparent p-0">
                <TabsTrigger value="overview" className={DIALOG_TAB_TRIGGER}>
                  Overview
                </TabsTrigger>
                <TabsTrigger value="timeline" className={DIALOG_TAB_TRIGGER}>
                  Status timeline
                </TabsTrigger>
                <TabsTrigger value="provider" className={DIALOG_TAB_TRIGGER}>
                  Provider
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-7 py-5">
                <TabsContent value="overview" className="m-0 space-y-5">
                  <section className="space-y-3">
                    <SectionHeading title="Refund record" />
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <DetailField label="Number" value={<span className="font-mono">{current?.refundNumber}</span>} />
                      <DetailField label="Amount" value={fmt.inr(Number(current?.amount ?? 0))} />
                      <DetailField label="Currency" value={current?.currency ?? 'INR'} />
                      <DetailField label="Type" value={current?.type} />
                      <DetailField label="Status" value={STATUS_LABEL[status]} />
                      <DetailField label="Created" value={fmt.dateTime(current?.createdAt)} />
                    </div>
                  </section>

                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Linked records" />
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <DetailField
                        label="Order"
                        value={
                          current?.salesOrder ? (
                            <span className="font-mono">{current.salesOrder.orderNumber}</span>
                          ) : (
                            '—'
                          )
                        }
                      />
                      <DetailField
                        label="Original payment"
                        value={
                          current?.payment ? (
                            <span className="font-mono">{current.payment.paymentNumber}</span>
                          ) : (
                            '—'
                          )
                        }
                      />
                      <DetailField
                        label="Linked return"
                        value={
                          current?.returnOrder ? (
                            <span className="font-mono">{current.returnOrder.returnNumber}</span>
                          ) : (
                            '—'
                          )
                        }
                      />
                    </div>
                  </section>

                  {current?.reason && (
                    <>
                      <Separator />
                      <section className="space-y-2">
                        <SectionHeading title="Reason" />
                        <p className="text-[13px] leading-relaxed text-foreground">{current.reason}</p>
                      </section>
                    </>
                  )}

                  {current?.failureReason && (
                    <>
                      <Separator />
                      <section className="space-y-2">
                        <SectionHeading title="Failure reason" />
                        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12.5px] text-destructive">
                          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>{current.failureReason}</span>
                        </div>
                      </section>
                    </>
                  )}
                </TabsContent>

                {/* ── Status timeline ─────────────────────────────── */}
                <TabsContent value="timeline" className="m-0 space-y-4">
                  <SectionHeading title="Status transitions" />
                  <ol className="space-y-1">
                    {(
                      [
                        ['REQUESTED', current?.createdAt],
                        ['APPROVED', null],
                        ['INITIATED', current?.initiatedAt],
                        ['PROCESSED', current?.processedAt],
                      ] as Array<[RefundStatus, string | null | undefined]>
                    ).map(([s, ts], idx, arr) => {
                      const reached = stageReached(status, s);
                      return (
                        <li key={s} className="relative pl-8 pb-4 last:pb-0">
                          <span
                            className={cn(
                              'absolute left-0 top-1 grid h-5 w-5 place-items-center rounded-full text-[10px] font-bold ring-4 ring-background',
                              reached ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground',
                            )}
                          >
                            {idx + 1}
                          </span>
                          {idx < arr.length - 1 && (
                            <span
                              className={cn(
                                'absolute left-[9px] top-6 h-full w-px',
                                reached ? 'bg-success/40' : 'bg-border',
                              )}
                            />
                          )}
                          <div className="text-[13px] font-medium text-foreground">
                            {STATUS_LABEL[s]}
                          </div>
                          <div className="text-[11.5px] text-muted-foreground">
                            {ts ? fmt.dateTime(ts) : reached ? '—' : 'Pending'}
                          </div>
                        </li>
                      );
                    })}
                  </ol>

                  {!isTerminal && (
                    <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-4">
                      {canMarkProcessed && (
                        <Button
                          size="sm"
                          onClick={() => update('PROCESSED')}
                          disabled={mutation.isPending}
                        >
                          <Check className="h-3.5 w-3.5" />
                          Mark processed
                        </Button>
                      )}
                      {canMarkFailed && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => update('FAILED')}
                          disabled={mutation.isPending}
                          className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                          Mark failed
                        </Button>
                      )}
                      {canReject && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => update('REJECTED')}
                          disabled={mutation.isPending}
                          className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                          Reject
                        </Button>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* ── Provider ────────────────────────────────────── */}
                <TabsContent value="provider" className="m-0 space-y-3">
                  <SectionHeading title="Provider integration" />
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <DetailField label="Provider" value={current?.provider ?? '—'} />
                    <DetailField label="Provider refund ID" value={current?.providerRefundId ?? '—'} />
                    <DetailField label="Provider payment ID" value={current?.providerPaymentId ?? '—'} />
                  </div>
                  <div className="rounded-md border border-info/20 bg-info/5 px-3 py-2.5 text-[12px] text-muted-foreground">
                    Gateway refund webhook updates the status here when the provider
                    confirms processing. Manual refunds skip this and require a manual
                    "Mark processed" once the funds clear.
                  </div>
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

const STATUS_RANK: Record<RefundStatus, number> = {
  REQUESTED: 0,
  APPROVED: 1,
  INITIATED: 2,
  PROCESSED: 3,
  PARTIAL_REFUND: 3,
  FAILED: -1,
  REJECTED: -1,
};

function stageReached(current: RefundStatus, stage: RefundStatus): boolean {
  if (STATUS_RANK[current] < 0) return stage === 'REQUESTED';
  return STATUS_RANK[current] >= STATUS_RANK[stage];
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[110px] px-4 py-3.5 first:pl-0 last:pr-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 font-mono text-[19px] leading-none tabular-nums',
          accent ? 'font-semibold text-foreground' : muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}
